/* ══════════════════════════════════════════════════════════════
   Portfolio.ts — Combined Broker Portfolio Page
   Aggregate holdings from Zerodha, Groww, Upstox
   ══════════════════════════════════════════════════════════════ */

import * as zerodha from '../broker/zerodha';
import * as groww from '../broker/groww';
import * as upstox from '../broker/upstox';
import { renderPhase2PortfolioEngine } from '../components/charts/Phase2PortfolioEngine';
import { isFeatureEnabled } from '../services/runtime-config';
import { t } from '../utils/i18n';

interface UnifiedHolding {
    symbol: string;
    broker: string;
    quantity: number;
    averagePrice: number;
    currentPrice: number;
    investedValue: number;
    currentValue: number;
    pnl: number;
    pnlPercent: number;
    dayChange: number;
}

export function renderPortfolio(): string {
    if (isFeatureEnabled('charts_v2_engine')) {
        return '<div id="portfolio-v2-root"></div>';
    }

    const brokers = [
        { name: 'Zerodha', icon: '🟢', connected: zerodha.isConnected(), color: '#387ed1' },
        { name: 'Groww', icon: '📱', connected: groww.isConnected(), color: '#5367ff' },
        { name: 'Upstox', icon: '📈', connected: upstox.isConnected(), color: '#7b2ff7' },
    ];

    const brokerCards = brokers.map(b => `
        <div class="glass-card" style="text-align: center; padding: var(--space-4);">
            <div style="font-size: 2rem; margin-bottom: var(--space-2);">${b.icon}</div>
            <h4 style="margin: 0 0 var(--space-2) 0;">${b.name}</h4>
            <div class="text-xs text-muted" style="margin-bottom: var(--space-3);">
                ${b.connected ? '✅ Connected' : '🔗 Not connected'}
            </div>
            <button class="glass-btn glass-btn--sm ${b.connected ? '' : 'glass-btn--primary'}"
                id="broker-${b.name.toLowerCase()}"
                style="width: 100%; ${b.connected ? '' : `background: ${b.color}; border-color: ${b.color}; color: white;`}">
                ${b.connected ? 'Disconnect' : 'Connect'}
            </button>
        </div>
    `).join('');

    return `
        <div style="display: flex; flex-direction: column; gap: var(--space-6);">
            <!-- Broker Connections -->
            <div>
                <h3 style="margin-bottom: var(--space-3);">🔗 ${t('portfolio.brokerConnections', undefined) !== 'portfolio.brokerConnections' ? t('portfolio.brokerConnections') : 'Broker Connections'}</h3>
                <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-4);">${brokerCards}</div>
            </div>

            <!-- Portfolio Summary -->
            <div class="glass-card">
                <h3 style="margin-bottom: var(--space-4);">💼 ${t('portfolio.summary', undefined) !== 'portfolio.summary' ? t('portfolio.summary') : 'Portfolio Summary'}</h3>
                <div id="portfolio-summary" style="display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--space-4);">
                    ${summaryCard('Total Invested', '₹0', '#9ca3af')}
                    ${summaryCard('Current Value', '₹0', '#f0f0f0')}
                    ${summaryCard('Total P&L', '₹0', '#10b981')}
                    ${summaryCard("Day's Change", '₹0', '#10b981')}
                </div>
            </div>

            <!-- Holdings Table -->
            <div class="glass-card">
                <h3 style="margin-bottom: var(--space-4);">📊 ${t('portfolio.holdings', undefined) !== 'portfolio.holdings' ? t('portfolio.holdings') : 'Holdings'}</h3>
                <div id="portfolio-holdings" style="overflow-x: auto;">
                    <table style="width: 100%; border-collapse: collapse;">
                        <thead>
                            <tr style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                                <th style="text-align: left; padding: var(--space-2); font-size: 0.75rem; color: var(--text-secondary);">Symbol</th>
                                <th style="text-align: left; padding: var(--space-2); font-size: 0.75rem; color: var(--text-secondary);">Broker</th>
                                <th style="text-align: right; padding: var(--space-2); font-size: 0.75rem; color: var(--text-secondary);">Qty</th>
                                <th style="text-align: right; padding: var(--space-2); font-size: 0.75rem; color: var(--text-secondary);">Avg Price</th>
                                <th style="text-align: right; padding: var(--space-2); font-size: 0.75rem; color: var(--text-secondary);">LTP</th>
                                <th style="text-align: right; padding: var(--space-2); font-size: 0.75rem; color: var(--text-secondary);">P&L</th>
                                <th style="text-align: right; padding: var(--space-2); font-size: 0.75rem; color: var(--text-secondary);">P&L %</th>
                            </tr>
                        </thead>
                        <tbody id="holdings-tbody">
                            <tr><td colspan="7" class="text-sm text-muted" style="padding: var(--space-4); text-align: center;">Loading holdings...</td></tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Sector Allocation -->
            <div class="glass-card">
                <h3 style="margin-bottom: var(--space-4);">🥧 Sector Allocation</h3>
                <div id="sector-allocation" style="display: flex; flex-wrap: wrap; gap: var(--space-2);">
                    <span class="text-sm text-muted">Connect a broker to view sector allocation</span>
                </div>
            </div>
        </div>
    `;
}

function summaryCard(label: string, value: string, color: string): string {
    return `
        <div style="text-align: center;">
            <div class="text-xs text-muted">${label}</div>
            <div style="font-size: 1.25rem; font-weight: 700; color: ${color}; margin-top: var(--space-1);">${value}</div>
        </div>
    `;
}

function formatINR(value: number): string {
    return '₹' + Math.abs(value).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function holdingRow(h: UnifiedHolding): string {
    const pnlColor = h.pnl >= 0 ? 'var(--color-emerald)' : 'var(--color-red)';
    const sign = h.pnl >= 0 ? '+' : '';
    return `
        <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
            <td style="padding: var(--space-2); font-weight: 600;">${h.symbol}</td>
            <td style="padding: var(--space-2);"><span class="text-xs text-muted">${h.broker}</span></td>
            <td style="padding: var(--space-2); text-align: right;">${h.quantity}</td>
            <td style="padding: var(--space-2); text-align: right;">${formatINR(h.averagePrice)}</td>
            <td style="padding: var(--space-2); text-align: right;">${formatINR(h.currentPrice)}</td>
            <td style="padding: var(--space-2); text-align: right; color: ${pnlColor};">${sign}${formatINR(h.pnl)}</td>
            <td style="padding: var(--space-2); text-align: right; color: ${pnlColor};">${sign}${h.pnlPercent.toFixed(2)}%</td>
        </tr>
    `;
}

export async function initPortfolio(): Promise<void> {
    if (isFeatureEnabled('charts_v2_engine')) {
        const root = document.getElementById('portfolio-v2-root');
        if (root) renderPhase2PortfolioEngine(root);
        return;
    }

    const allHoldings: UnifiedHolding[] = [];

    // Fetch from all connected brokers
    const zHoldings = await zerodha.getHoldings();
    zHoldings.forEach(h => allHoldings.push({
        symbol: h.symbol, broker: 'Zerodha', quantity: h.quantity,
        averagePrice: h.averagePrice, currentPrice: h.lastPrice,
        investedValue: h.investedValue, currentValue: h.currentValue,
        pnl: h.pnl, pnlPercent: h.pnlPercent, dayChange: h.dayChange,
    }));

    const gHoldings = await groww.getStockHoldings();
    gHoldings.forEach(h => allHoldings.push({
        symbol: h.symbol, broker: 'Groww', quantity: h.quantity,
        averagePrice: h.averagePrice, currentPrice: h.currentPrice,
        investedValue: h.investedValue, currentValue: h.currentValue,
        pnl: h.pnl, pnlPercent: h.pnlPercent, dayChange: h.dayChange,
    }));

    const uHoldings = await upstox.getHoldings();
    uHoldings.forEach(h => allHoldings.push({
        symbol: h.symbol, broker: 'Upstox', quantity: h.quantity,
        averagePrice: h.averagePrice, currentPrice: h.lastPrice,
        investedValue: h.investedValue, currentValue: h.currentValue,
        pnl: h.pnl, pnlPercent: 0, dayChange: h.dayChange,
    }));

    // Update holdings table
    const tbody = document.getElementById('holdings-tbody');
    if (tbody && allHoldings.length > 0) {
        tbody.innerHTML = allHoldings.map(holdingRow).join('');
    }

    // Update summary
    const totalInvested = allHoldings.reduce((sum, h) => sum + h.investedValue, 0);
    const totalCurrent = allHoldings.reduce((sum, h) => sum + h.currentValue, 0);
    const totalPnl = totalCurrent - totalInvested;
    const totalDayChange = allHoldings.reduce((sum, h) => sum + (h.dayChange * h.quantity), 0);

    const summary = document.getElementById('portfolio-summary');
    if (summary) {
        const pnlColor = totalPnl >= 0 ? '#10b981' : '#ef4444';
        const dayColor = totalDayChange >= 0 ? '#10b981' : '#ef4444';
        summary.innerHTML = `
            ${summaryCard('Total Invested', formatINR(totalInvested), '#9ca3af')}
            ${summaryCard('Current Value', formatINR(totalCurrent), '#f0f0f0')}
            ${summaryCard('Total P&L', `${totalPnl >= 0 ? '+' : '-'}${formatINR(totalPnl)}`, pnlColor)}
            ${summaryCard("Day's Change", `${totalDayChange >= 0 ? '+' : '-'}${formatINR(totalDayChange)}`, dayColor)}
        `;
    }
}
